import { forwardRef } from 'react';
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Table — tabela estilizada do design system.
 *
 * Composição:
 *   <Table>
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead>Cliente</TableHead>
 *         <TableHead>Valor</TableHead>
 *         <TableHead align="right">Status</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody>
 *       <TableRow>
 *         <TableCell>Saint Michael</TableCell>
 *         <TableCell>R$ 24.800</TableCell>
 *         <TableCell align="right"><Badge variant="warning">Aberto</Badge></TableCell>
 *       </TableRow>
 *     </TableBody>
 *   </Table>
 *
 * Estilo: editorial calmo — sem bordas pesadas, separação por linhas sutis.
 */

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="w-full overflow-x-auto">
      <table
        ref={ref}
        className={cn('w-full text-sm border-collapse', className)}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('', className)} {...props} />
  )
);
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('', className)} {...props} />
);
TableBody.displayName = 'TableBody';

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-line-soft dark:border-line-soft-dark transition-colors',
        '[&:not(thead_tr)]:hover:bg-paper-warm dark:[&:not(thead_tr)]:hover:bg-paper-dark-warm',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align = 'left', ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // FASE-B: 10px → 12px (piso tipográfico) + sticky no scroll da tabela.
        'px-4 py-3 text-xs uppercase tracking-wider font-semibold text-ink-3 dark:text-ink-dark-3',
        'sticky top-0 bg-paper-warm dark:bg-paper-dark-warm',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align = 'left', ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-4 py-3 text-ink-1 dark:text-ink-dark-1',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';
